import type { LitElement, ReactiveController } from "lit";
import { i18n } from "../i18n";

export function moveColumn(
  order: readonly number[],
  from: number,
  to: number,
): number[] {
  const next = [...order];
  const source = next.indexOf(from);
  const target = next.indexOf(to);
  if (source < 0 || target < 0) return next;
  next.splice(source, 1);
  next.splice(target, 0, from);
  return next;
}

export const MIN_COLUMN_WIDTH = 72;
export function resizedColumnWidth(start: number, delta: number): number {
  return Math.max(MIN_COLUMN_WIDTH, Math.round(start + delta));
}

type ResizeState = {
  handle: HTMLElement;
  table: HTMLTableElement;
  pointer: number;
  column: number;
  x: number;
  width: number;
  previous?: number[];
};

/** Restore Lit's exact node topology before updates; keep widths by column identity. */
export class TableColumns implements ReactiveController {
  private snapshots: { row: HTMLTableRowElement; nodes: Node[] }[] = [];
  private orders = new Map<string, number[]>();
  private source: HTMLTableCellElement | null = null;
  private widths = new Map<string, number[]>();
  private decorations: {
    table: HTMLTableElement;
    group: HTMLTableColElement;
    style: string;
    handles: HTMLElement[];
  }[] = [];
  private resizing: ResizeState | null = null;

  constructor(private host: LitElement) {
    host.addController(this);
  }

  hostConnected() {
    this.host.addEventListener("dragstart", this.start);
    this.host.addEventListener("dragover", this.over);
    this.host.addEventListener("drop", this.drop);
    this.host.addEventListener("dragend", this.end);
    this.host.addEventListener("keydown", this.keydown);
    this.host.addEventListener("pointerdown", this.resizeStart);
    this.host.addEventListener("pointermove", this.resizeMove);
    this.host.addEventListener("pointerup", this.resizeEnd);
    this.host.addEventListener("pointercancel", this.resizeCancel);
    this.host.addEventListener("lostpointercapture", this.resizeCancel);
    this.host.addEventListener("click", this.resizeClick, true);
  }

  hostDisconnected() {
    this.host.removeEventListener("dragstart", this.start);
    this.host.removeEventListener("dragover", this.over);
    this.host.removeEventListener("drop", this.drop);
    this.host.removeEventListener("dragend", this.end);
    this.host.removeEventListener("keydown", this.keydown);
    this.host.removeEventListener("pointerdown", this.resizeStart);
    this.host.removeEventListener("pointermove", this.resizeMove);
    this.host.removeEventListener("pointerup", this.resizeEnd);
    this.host.removeEventListener("pointercancel", this.resizeCancel);
    this.host.removeEventListener("lostpointercapture", this.resizeCancel);
    this.host.removeEventListener("click", this.resizeClick, true);
    this.restore();
  }

  hostUpdate() {
    this.restore();
  }

  private restore() {
    this.finishResize(true);
    this.source = null;
    for (const { table, group, style, handles } of this.decorations) {
      for (const handle of handles) handle.remove();
      group.remove();
      table.style.cssText = style;
      delete table.dataset.columnsSized;
    }
    this.decorations = [];
    for (const { row, nodes } of this.snapshots) row.replaceChildren(...nodes);
    this.snapshots = [];
  }

  hostUpdated() {
    for (const table of this.host.querySelectorAll<HTMLTableElement>(
      "table.data-table",
    )) {
      const headers = [...(table.tHead?.rows[0]?.cells || [])];
      if (headers.length < 2 || headers.some((cell) => cell.colSpan !== 1))
        continue;
      const signature = headers
        .map(
          (cell) =>
            cell.dataset.columnKey ||
            cell.textContent?.replace(/[▲▼↕]/g, "").trim(),
        )
        .join("|");
      const order =
        this.orders.get(signature) || headers.map((_, index) => index);
      table.dataset.columnOrderKey = signature;
      const group = document.createElement("colgroup");
      const handles: HTMLElement[] = [];
      this.decorations.push({
        table,
        group,
        style: table.style.cssText,
        handles,
      });
      table.prepend(group);
      headers.forEach((cell, index) => {
        cell.draggable = true;
        cell.dataset.columnIndex = String(index);
        cell.tabIndex = 0;
        cell.title = i18n.t("tableColumnMoveHelp");
        const handle = document.createElement("span");
        handle.className = "table-column-resizer";
        handle.tabIndex = 0;
        handle.draggable = false;
        handle.setAttribute("role", "separator");
        handle.setAttribute("aria-orientation", "vertical");
        handle.setAttribute("aria-label", i18n.t("tableColumnResizeHelp"));
        handle.setAttribute("aria-valuemin", String(MIN_COLUMN_WIDTH));
        handle.title = i18n.t("tableColumnResizeHelp");
        cell.append(handle);
        handles.push(handle);
      });
      for (const index of order) {
        const col = document.createElement("col");
        col.dataset.columnIndex = String(index);
        group.append(col);
      }
      for (const row of table.rows) {
        const cells = [...row.cells];
        if (
          cells.length !== headers.length ||
          cells.some((cell) => cell.colSpan !== 1)
        )
          continue;
        this.snapshots.push({ row, nodes: [...row.childNodes] });
        for (const index of order) row.append(cells[index]);
      }
      this.applyWidths(table);
    }
  }

  private resizeHandle(event: Event): HTMLElement | null {
    return event.target instanceof Element
      ? event.target.closest<HTMLElement>(".table-column-resizer")
      : null;
  }

  private applyWidths(table: HTMLTableElement) {
    const values = this.widths.get(table.dataset.columnOrderKey!);
    const decoration = this.decorations.find((item) => item.table === table);
    if (!decoration) return;
    table.style.cssText = decoration.style;
    delete table.dataset.columnsSized;
    if (values) {
      table.dataset.columnsSized = "true";
      table.style.tableLayout = "fixed";
      table.style.width = `${values.reduce((sum, width) => sum + width, 0)}px`;
      table.style.minWidth = table.style.width;
    }
    for (const col of decoration.group.children) {
      const element = col as HTMLTableColElement;
      element.style.width = values
        ? `${values[Number(element.dataset.columnIndex)]}px`
        : "";
    }
    for (const handle of decoration.handles) {
      const cell = handle.parentElement as HTMLTableCellElement;
      const width =
        values?.[Number(cell.dataset.columnIndex)] ||
        cell.getBoundingClientRect().width;
      handle.setAttribute("aria-valuenow", String(Math.round(width)));
    }
  }

  private measureWidths(table: HTMLTableElement): number[] {
    const values: number[] = [];
    for (const cell of table.tHead!.rows[0].cells)
      values[Number(cell.dataset.columnIndex)] = resizedColumnWidth(
        cell.getBoundingClientRect().width,
        0,
      );
    return values;
  }

  private resizeStart = (event: PointerEvent) => {
    const handle = this.resizeHandle(event);
    const cell = this.header(event);
    if (!handle || !cell || event.button !== 0 || this.resizing) return;
    event.preventDefault();
    event.stopPropagation();
    const table = cell.closest("table")!;
    const key = table.dataset.columnOrderKey!;
    const previous = this.widths.get(key);
    const values = previous ? [...previous] : this.measureWidths(table);
    const column = Number(cell.dataset.columnIndex);
    this.source = null;
    this.widths.set(key, values);
    this.resizing = {
      handle,
      table,
      pointer: event.pointerId,
      column,
      x: event.clientX,
      width: values[column],
      previous: previous ? [...previous] : undefined,
    };
    handle.setPointerCapture(event.pointerId);
    handle.classList.add("is-resizing");
    this.applyWidths(table);
  };

  private resizeMove = (event: PointerEvent) => {
    const state = this.resizing;
    if (!state || event.pointerId !== state.pointer) return;
    event.preventDefault();
    const values = this.widths.get(state.table.dataset.columnOrderKey!)!;
    values[state.column] = resizedColumnWidth(
      state.width,
      event.clientX - state.x,
    );
    this.applyWidths(state.table);
  };

  private finishResize(cancel: boolean) {
    const state = this.resizing;
    if (!state) return;
    this.resizing = null;
    if (cancel) {
      const key = state.table.dataset.columnOrderKey!;
      if (state.previous) this.widths.set(key, state.previous);
      else this.widths.delete(key);
      this.applyWidths(state.table);
    }
    state.handle.classList.remove("is-resizing");
    if (state.handle.hasPointerCapture(state.pointer))
      state.handle.releasePointerCapture(state.pointer);
  }

  private resizeEnd = (event: PointerEvent) => {
    if (event.pointerId === this.resizing?.pointer) this.finishResize(false);
  };
  private resizeCancel = (event: PointerEvent) => {
    if (event.pointerId === this.resizing?.pointer) this.finishResize(true);
  };
  private resizeClick = (event: Event) => {
    if (!this.resizeHandle(event)) return;
    event.preventDefault();
    event.stopPropagation();
  };

  private header(event: Event) {
    return event.target instanceof Element
      ? event.target.closest<HTMLTableCellElement>("th[data-column-index]")
      : null;
  }
  private start = (event: DragEvent) => {
    if (this.resizing || this.resizeHandle(event)) {
      event.preventDefault();
      return;
    }
    this.source = this.header(event);
    if (this.source && event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(
        "text/plain",
        this.source.dataset.columnIndex || "",
      );
    }
  };
  private over = (event: DragEvent) => {
    const target = this.header(event);
    if (
      this.source &&
      target?.closest("table") === this.source.closest("table")
    )
      event.preventDefault();
  };
  private end = () => {
    this.source = null;
  };
  private drop = (event: DragEvent) => {
    const target = this.header(event);
    if (this.source && target) {
      event.preventDefault();
      this.move(this.source, target);
    }
    this.source = null;
  };
  private move(source: HTMLTableCellElement, target: HTMLTableCellElement) {
    const table = source.closest("table");
    if (!table || table !== target.closest("table")) return;
    const key = table.dataset.columnOrderKey!;
    const count = table.tHead!.rows[0].cells.length;
    const order =
      this.orders.get(key) || Array.from({ length: count }, (_, i) => i);
    this.orders.set(
      key,
      moveColumn(
        order,
        Number(source.dataset.columnIndex),
        Number(target.dataset.columnIndex),
      ),
    );
    this.host.requestUpdate();
  }
  private keydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && this.resizing) {
      event.preventDefault();
      this.finishResize(true);
      return;
    }
    if (
      this.resizeHandle(event) &&
      ["ArrowLeft", "ArrowRight"].includes(event.key)
    ) {
      const cell = this.header(event)!;
      const table = cell.closest("table")!;
      const key = table.dataset.columnOrderKey!;
      const values = [...(this.widths.get(key) || this.measureWidths(table))];
      const index = Number(cell.dataset.columnIndex);
      event.preventDefault();
      event.stopPropagation();
      values[index] = resizedColumnWidth(
        values[index],
        (event.key === "ArrowLeft" ? -1 : 1) * (event.shiftKey ? 50 : 10),
      );
      this.widths.set(key, values);
      this.applyWidths(table);
      return;
    }
    if (!event.altKey || !["ArrowLeft", "ArrowRight"].includes(event.key))
      return;
    const header = this.header(event);
    if (!header) return;
    const target =
      event.key === "ArrowLeft"
        ? header.previousElementSibling
        : header.nextElementSibling;
    if (target instanceof HTMLTableCellElement) {
      event.preventDefault();
      this.move(header, target);
      void this.host.updateComplete.then(() => header.focus());
    }
  };
}
